import { z } from 'zod';

/**
 * Client error reports (F7 AC-2.4, T-2.5): `POST /api/client-errors`, grouped by fingerprint in the
 * analytics DB and listed on the admin "Lỗi" page.
 *
 * Errors are operational data, not usage tracking, but a message can still quote what the user typed or
 * was shown (an e-mail, a phone number, a token in a URL). So the text is scrubbed twice with the SAME
 * functions below: best effort in the browser before sending (packages/platform, apps/admin) and
 * authoritatively in the Worker before storing (apps/api/src/routes/client-errors.ts). No IP, cookie,
 * user id or e-mail is ever stored.
 *
 * The functions are pure and dependency-free (regexes only), so they run in the Worker, the browser and
 * tests. They are conservative: when in doubt a token is replaced, never kept.
 */

/** Frontends that report errors. The static site does not. */
export const CLIENT_ERROR_APPS = ['app', 'admin'] as const;
export type ClientErrorApp = (typeof CLIENT_ERROR_APPS)[number];

/** Request body cap, checked before parsing (413 above it). */
export const CLIENT_ERROR_BODY_MAX_BYTES = 8 * 1024;
/** Stored limits, in UTF-8 bytes; they equal the CHECK constraints of `client_errors`. */
export const CLIENT_ERROR_MESSAGE_MAX_BYTES = 1024;
export const CLIENT_ERROR_ROUTE_MAX_BYTES = 256;
export const CLIENT_ERROR_STACK_MAX_BYTES = 2048;
/** Frames kept in the stored stack ("top frames only"). */
export const CLIENT_ERROR_STACK_FRAMES = 5;

/** Schema limits are in characters and looser than the stored ones: the server scrubs, then truncates. */
const MESSAGE_CHARS = 2000;
const STACK_CHARS = 4000;
const ROUTE_CHARS = 512;

export const ClientErrorRequestSchema = z.strictObject({
  app: z.enum(CLIENT_ERROR_APPS),
  message: z.string().trim().min(1).max(MESSAGE_CHARS),
  /** Top stack frames only. */
  stack: z.string().max(STACK_CHARS).optional(),
  /** Route path of the page; a query string or fragment is stripped server-side. */
  route: z.string().max(ROUTE_CHARS),
});
export type ClientErrorRequest = z.infer<typeof ClientErrorRequestSchema>;

// ---- scrubbing ------------------------------------------------------------------------------------

/** UTF-8 bytes of one code point. */
const utf8Size = (codePoint: number): number =>
  codePoint < 0x80 ? 1 : codePoint < 0x800 ? 2 : codePoint < 0x10000 ? 3 : 4;

/** Longest prefix of `text` that fits in `maxBytes` of UTF-8, never splitting a code point. */
export function truncateUtf8(text: string, maxBytes: number): string {
  let bytes = 0;
  let end = 0;
  for (const ch of text) {
    const size = utf8Size(ch.codePointAt(0) ?? 0);
    if (bytes + size > maxBytes) return text.slice(0, end);
    bytes += size;
    end += ch.length;
  }
  return text;
}

const URL_END = String.raw`[^\s"'<>)\]}]*`;

/** Rules applied in order; earlier rules must run before the generic digit and token ones. */
const SCRUB_RULES: readonly (readonly [RegExp, string])[] = [
  // URLs and paths: no query string, no fragment (they carry tokens, ids, search text).
  [new RegExp(String.raw`(https?:\/\/[^\s"'<>?#)\]}]*)[?#]${URL_END}`, 'gi'), '$1'],
  [new RegExp(String.raw`(\/[\w.~%@+-]*)[?#]${URL_END}`, 'g'), '$1'],
  // Home folders name a person: /Users/<name>/, /home/<name>/, C:\Users\<name>\.
  [/(\/Users\/|\/home\/)[^/\s:)"']+/g, '$1<user>'],
  [/([A-Za-z]:\\+Users\\+)[^\\\s:)"']+/gi, '$1<user>'],
  // Credentials.
  [/\bBearer\s+[\w.~+/=-]+/gi, 'Bearer <token>'],
  [
    /\b(token|secret|password|passwd|pwd|api[_-]?key|apikey|authorization|session|otp|cookie)(["']?\s*[:=]\s*["']?)[^\s"'&,;)}\]]+/gi,
    '$1$2<token>',
  ],
  // JWT-like (header.payload[.signature]) and any dotted triple of long base64url chunks.
  [/\beyJ[\w-]{4,}\.[\w-]{4,}(?:\.[\w-]*)?/g, '<token>'],
  [/\b[\w-]{12,}\.[\w-]{12,}\.[\w-]{12,}\b/g, '<token>'],
  // E-mail addresses.
  [/[\w.%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/gi, '<email>'],
  // UUIDs.
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<id>'],
  // IPv4 addresses.
  [/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>'],
  // Hex tokens: 16+ hex digits, or 8+ mixing digits and letters (ids, hashes, session ids).
  [/\b[0-9a-f]{16,}\b/gi, '<token>'],
  [/\b(?=[0-9a-f]*\d)(?=[0-9a-f]*[a-f])[0-9a-f]{8,}\b/gi, '<token>'],
  // Long mixed alphanumeric strings (base64url ids, API keys).
  [/\b(?=[\w-]*\d)(?=[\w-]*[A-Za-z])[\w-]{24,}\b/g, '<token>'],
  // Phone numbers (VN: 0xxx xxx xxx, +84 ...) and other digit runs of 9+ digits, with separators.
  [/(?<![\w.])\+?\d(?:[\s.()-]?\d){8,}(?!\w)/g, '<phone>'],
  // Any other run of 6+ digits (ids, order numbers).
  [/\d{6,}/g, '<n>'],
];

/** Removes personal data and secrets from free text; keeps the rest as it is. */
export function scrubText(text: string): string {
  // Control characters (except tab/newline) out; they only make a message unreadable.
  // eslint-disable-next-line no-control-regex
  let out = text.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '');
  for (const [pattern, replacement] of SCRUB_RULES) out = out.replace(pattern, replacement);
  return out;
}

/**
 * The grouping form of a message: scrubbed, every remaining number collapsed to `<n>` ("Question 12"
 * and "Question 7" are one error), whitespace collapsed, trimmed, at most 1 KB of UTF-8.
 */
export function normaliseMessage(message: string): string {
  const collapsed = scrubText(message)
    .replace(/\d+(?:[.,]\d+)*/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim();
  return truncateUtf8(collapsed, CLIENT_ERROR_MESSAGE_MAX_BYTES);
}

/** `/path` of a route or URL: scheme, host, query string and fragment dropped, at most 256 bytes. */
export function normaliseRoute(route: string): string {
  let path = route.trim();
  path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
  path = path.replace(/[?#].*$/s, '');
  path = scrubText(path);
  if (path === '') path = '/';
  else if (!path.startsWith('/')) path = `/${path}`;
  return truncateUtf8(path, CLIENT_ERROR_ROUTE_MAX_BYTES);
}

/** Build hash of a bundled file name (`index-Ab12Cd34.js`, 8 chars): dropped, it would only split groups. */
const ASSET_HASH = /-[A-Za-z0-9_]{8}(?=\.m?js\b)/g;

/** A stack line that names a call site (`at fn (url:1:2)`, `fn@url:1:2`, `url:1:2`), not the message line. */
const isFrame = (line: string) => /^at\s/.test(line) || /:\d+(?::\d+)?\)?$/.test(line);

/** Frame lines of a stack, trimmed, in order (the message line of V8 stacks is skipped). */
function frames(stack: string): string[] {
  return stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '' && isFrame(line));
}

/**
 * The stored stack: the first {@link CLIENT_ERROR_STACK_FRAMES} frames, scrubbed (URLs lose their query
 * and build hash; line and column stay), at most 2 KB.
 */
export function normaliseStack(stack: string | undefined): string {
  if (stack === undefined) return '';
  const kept = frames(stack)
    .slice(0, CLIENT_ERROR_STACK_FRAMES)
    .map((line) => scrubText(line.replace(ASSET_HASH, '')));
  return truncateUtf8(kept.join('\n'), CLIENT_ERROR_STACK_MAX_BYTES);
}

/**
 * The first frame without line and column, origin or build hash, so one error stays one group across
 * releases and bundlers: `at render (https://x.pages.dev/assets/index-Ab12Cd34.js:10:20)` becomes
 * `at render (/assets/index.js)`. `''` when the stack has no frame.
 */
export function topFrame(stack: string | undefined): string {
  if (stack === undefined) return '';
  const [first] = frames(stack);
  if (first === undefined) return '';
  return scrubText(first.replace(ASSET_HASH, ''))
    .replace(/:\d+(?::\d+)?(?=\)?$)/, '')
    .replace(/https?:\/\/[^/\s)]+/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The text that is hashed into the fingerprint (the Worker hashes it; the browser dedupes by it). */
export function fingerprintSource(
  app: ClientErrorApp,
  message: string,
  stack: string | undefined,
): string {
  return `${app}\n${normaliseMessage(message)}\n${topFrame(stack)}`;
}
